import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../assets/compressedLogo.png';
import '../styles/Header.css';

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef(null);
  const navRef = useRef(null);
  const buttonRef = useRef(null);

  // Détecter le scroll pour l'ombre du header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Bloquer le scroll du body quand le menu mobile est ouvert
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  // Définit la variable CSS --header-height pour que le padding du nav corresponde exactement
  useEffect(() => {
    const setHeaderHeight = () => {
      const h = headerRef.current ? headerRef.current.getBoundingClientRect().height : 64;
      document.documentElement.style.setProperty('--header-height', `${Math.round(h)}px`);
    };
    setHeaderHeight();
    window.addEventListener('resize', setHeaderHeight);
    return () => window.removeEventListener('resize', setHeaderHeight);
  }, []);

  // Fermer sur Escape + restaurer focus au bouton
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobileMenuOpen]);

  // Close and restore focus when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: t('nav.home') || 'Accueil' },
    { path: '/about', label: t('nav.about') || 'CV' },
    { path: '/portfolio', label: t('nav.portfolio') || 'Portfolio' },
    { path: '/contact-client', label: t('nav.project') || 'Projet' },
    { path: '/contact-entreprise', label: t('nav.recruit') || 'Recrutement' }
  ];

  const isActive = (path) => location.pathname === path;

  const toggleMobileMenu = () => setIsMobileMenuOpen((s) => !s);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <header ref={headerRef} className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <Link to="/" className="logo" onClick={closeMobileMenu}>
          <img src={Logo} alt="DreamDev Logo" className="logo-image" />
        </Link>

        <nav
          ref={navRef}
          className={`nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}
          aria-hidden={!isMobileMenuOpen}
          tabIndex={isMobileMenuOpen ? 0 : -1}
        >
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              {link.label}
            </Link>
          ))}

          {/* <div className="nav-language-switcher">
            <LanguageSwitcher />
          </div> */}
        </nav>

        <div className="header-actions">
          {/* Desktop switcher (hidden on mobile via CSS) */}
          {/* <LanguageSwitcher /> */}

          <button
            ref={buttonRef}
            className={`mobile-menu-btn ${isMobileMenuOpen ? 'open' : ''}`}
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="main-navigation"
          >
            <span className="menu-line" />
            <span className="menu-line" />
            <span className="menu-line" />
          </button>
        </div>
      </div>

      {/* Overlay (close on click) */}
      <div
        className={`mobile-overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
        aria-hidden={!isMobileMenuOpen}
      />
    </header>
  );
};

export default Header;
